"use client";

import Image from "next/image";
import { usePathname } from "next/navigation";
import { storyblokEditable, type SbBlokData } from "@storyblok/react/rsc";

import { useTriggerAction, type BlokAction } from "@/lib/action-link";
import { resolveBg } from "@/lib/bg-color";
import { regionFromPathname, type Region } from "@/lib/region";
import { mergeSourced, type SourcedBlok } from "@/lib/sourced-blok";
import {
  resolveLink,
  resolveRichTextHref,
  type StoryblokLink,
} from "@/lib/storyblok-link";

type Asset = { filename?: string; alt?: string };

type RichTextMark = {
  type: string;
  attrs?: { href?: string; target?: string };
};
type RichTextNode = {
  type: string;
  text?: string;
  content?: RichTextNode[];
  marks?: RichTextMark[];
};
type RichTextDoc = { type: "doc"; content?: RichTextNode[] };

type GlobalTextWithImageListFields = {
  title?: string;
  description?: string | RichTextDoc;
  button_label?: string;
  button_link?: StoryblokLink;
  button_action?: BlokAction | "";
  button_style?: "text_link" | "solid";
  side_content?: "image" | "list";
  image?: Asset;
  image_size?: "large" | "small";
  list_items?: string;
  list_tick_color?: "green" | "purple";
  list_in_card?: boolean;
  reverse_order?: boolean;
  bg_color?: unknown;
};

type GlobalTextWithImageListBlok = SbBlokData &
  SourcedBlok<GlobalTextWithImageListFields>;

const FIELDS: (keyof GlobalTextWithImageListFields)[] = [
  "title",
  "description",
  "button_label",
  "button_link",
  "button_action",
  "button_style",
  "side_content",
  "image",
  "image_size",
  "list_items",
  "list_tick_color",
  "list_in_card",
  "reverse_order",
  "bg_color",
];

function richTextHasContent(node: RichTextNode | undefined): boolean {
  if (!node) return false;
  if (node.type === "text") return !!node.text?.trim();
  return (node.content ?? []).some(richTextHasContent);
}

function renderNode(
  node: RichTextNode,
  key: number,
  onDark: boolean,
  region?: Region,
): React.ReactNode {
  switch (node.type) {
    case "paragraph":
      return <p key={key}>{node.content?.map((n, i) => renderNode(n, i, onDark, region))}</p>;
    case "hard_break":
      return <br key={key} />;
    case "bullet_list":
      return (
        <ul key={key} className="ml-5 list-disc space-y-1">
          {node.content?.map((n, i) => renderNode(n, i, onDark, region))}
        </ul>
      );
    case "ordered_list":
      return (
        <ol key={key} className="ml-5 list-decimal space-y-1">
          {node.content?.map((n, i) => renderNode(n, i, onDark, region))}
        </ol>
      );
    case "list_item":
      return (
        <li key={key}>
          {node.content?.map((n, i) =>
            n.type === "paragraph"
              ? n.content?.map((c, j) => renderNode(c, j, onDark, region))
              : renderNode(n, i, onDark, region),
          )}
        </li>
      );
    case "text": {
      let el: React.ReactNode = node.text ?? "";
      for (const mark of node.marks ?? []) {
        if (mark.type === "bold") el = <strong>{el}</strong>;
        else if (mark.type === "italic") el = <em>{el}</em>;
        else if (mark.type === "link" && mark.attrs?.href && (node.text ?? "").trim()) {
          const href = resolveRichTextHref(mark.attrs.href, region);
          const isExternal = /^https?:\/\//i.test(href);
          const target = isExternal ? "_blank" : mark.attrs.target;
          el = (
            <a
              href={href}
              target={target}
              rel={target === "_blank" ? "noopener noreferrer" : undefined}
              className={`underline underline-offset-2 hover:opacity-80 ${
                onDark ? "text-white" : "text-purple-03"
              }`}
            >
              {el}
            </a>
          );
        }
      }
      return <span key={key}>{el}</span>;
    }
    default:
      return node.content?.map((n, i) => renderNode(n, i, onDark, region)) ?? null;
  }
}

function Description({
  value,
  onDark,
  region,
}: {
  value: string | RichTextDoc;
  onDark: boolean;
  region?: Region;
}) {
  const baseClass = `text-body leading-[1.56] ${onDark ? "text-white/85" : "text-[#595959]"}`;
  if (typeof value === "string") {
    return value.trim() ? <p className={baseClass}>{value}</p> : null;
  }
  if (!richTextHasContent(value)) return null;
  return (
    <div className={`flex flex-col gap-3 ${baseClass}`}>
      {value.content?.map((n, i) => renderNode(n, i, onDark, region))}
    </div>
  );
}

function ArrowIcon() {
  return (
    <svg
      width="17"
      height="11"
      viewBox="0 0 17 11"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M1 5.5h14.5M11 1l4.5 4.5L11 10"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function TickIcon({ className }: { className?: string }) {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 26 26"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
      className={className}
    >
      <circle cx="13" cy="13" r="13" fill="currentColor" />
      <path
        d="M7.4 13.3l3.4 3.4 7.8-8"
        stroke="#fff"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function GlobalTextWithImageList({
  blok: rawBlok,
}: {
  blok: GlobalTextWithImageListBlok;
}) {
  const blok = mergeSourced(rawBlok, FIELDS);
  const pathname = usePathname();
  const region = regionFromPathname(pathname);
  const { bg, onDark } = resolveBg(blok.bg_color, "white");

  const link = resolveLink(blok.button_link, region);
  const triggerAction = useTriggerAction();
  const action = blok.button_action || null;
  const showButton = Boolean(blok.button_label && (action || link.href !== "#"));
  const isTextLink = blok.button_style !== "solid";
  const buttonClass = isTextLink
    ? `mt-2 inline-flex w-fit items-center gap-[7px] py-2.5 text-body font-extrabold leading-[1.054] hover:underline ${
        onDark ? "text-white" : "text-purple-03"
      }`
    : "mt-2 hidden h-14 w-fit items-center justify-center rounded-[8px] bg-purple-03 px-7 text-body font-bold text-white transition-colors hover:bg-purple-04 md:inline-flex";
  const buttonInner = isTextLink ? (
    <>
      {blok.button_label}
      <ArrowIcon />
    </>
  ) : (
    blok.button_label
  );

  const sideOrder = blok.reverse_order ? "lg:order-1" : "lg:order-2";
  const textOrder = blok.reverse_order ? "lg:order-2" : "lg:order-1";

  const isList = blok.side_content === "list";
  const small = blok.image_size === "small";

  const items = (blok.list_items || "")
    .split("\n")
    .map((s) => s.trim())
    .filter(Boolean);

  const hasImage = !isList && Boolean(blok.image?.filename);
  const hasList = isList && items.length > 0;

  const sideWidth = isList
    ? "lg:w-1/2 xl:w-[604px] xl:shrink-0"
    : small
      ? "lg:w-[40%] xl:w-[384px] xl:shrink-0"
      : "lg:w-[47%] xl:w-[600px] xl:shrink-0";

  const tickColor = blok.list_tick_color === "purple" ? "text-purple-05" : "text-forest-01";
  // Card is always light; bare list follows section theme.
  const listTextDark = onDark && !blok.list_in_card;

  return (
    <section {...storyblokEditable(blok)} className={bg}>
      <div className="mx-auto w-full max-w-[1280px] px-[clamp(1.25rem,4vw,2.5rem)] py-[clamp(2.5rem,5vw,5rem)]">
        <div className="flex flex-col gap-[38px] lg:flex-row lg:items-center lg:gap-12 xl:gap-[156px]">
          <div className={`flex flex-col gap-5 lg:min-w-0 lg:flex-1 ${textOrder}`}>
            {blok.title && (
              <h2
                className={`text-[clamp(2.25rem,4vw,3rem)] leading-[1.08] font-bold ${
                  onDark ? "text-white" : "text-purple-05"
                }`}
              >
                {blok.title}
              </h2>
            )}

            {blok.description && (
              <Description value={blok.description} onDark={onDark} region={region} />
            )}

            {showButton &&
              (action ? (
                <button
                  type="button"
                  onClick={(e) => triggerAction(action, e)}
                  className={`cursor-pointer ${buttonClass}`}
                >
                  {buttonInner}
                </button>
              ) : (
                <a
                  href={link.href}
                  {...(link.target ? { target: link.target } : {})}
                  rel={link.target === "_blank" ? "noopener noreferrer" : undefined}
                  className={buttonClass}
                >
                  {buttonInner}
                </a>
              ))}
          </div>

          {hasImage && blok.image?.filename && (
            <div className={`order-first w-full ${sideWidth} ${sideOrder}`}>
              <div
                className={`relative mx-auto w-full max-w-[440px] overflow-hidden rounded-[24px] md:max-w-none ${
                  small ? "aspect-384/295" : "aspect-600/402"
                }`}
              >
                <Image
                  src={blok.image.filename}
                  alt={blok.image.alt || ""}
                  fill
                  sizes={
                    small
                      ? "(min-width: 1280px) 384px, (min-width: 1115px) 40vw, 100vw"
                      : "(min-width: 1280px) 600px, (min-width: 1115px) 47vw, 100vw"
                  }
                  className="object-cover"
                />
              </div>
            </div>
          )}

          {hasList && (
            <div className={`w-full ${sideWidth} ${sideOrder}`}>
              <ul
                className={`flex flex-col gap-5 ${
                  blok.list_in_card ? "bg-dusk-01 rounded-[24px] p-[clamp(1.5rem,3vw,2.5rem)]" : ""
                }`}
              >
                {items.map((item, i) => (
                  <li key={`${i}-${item}`} className="flex items-start gap-4">
                    <TickIcon className={`mt-px shrink-0 ${tickColor}`} />
                    <span
                      className={`text-body leading-[1.56] ${
                        listTextDark ? "text-white/85" : "text-[#595959]"
                      }`}
                    >
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
